import { EditorState } from 'draft-js';
import { produce } from 'immer';
import { setDefaultInlineStyleIfNeeded } from '../components/RichEditor';
import {
  isATextBox,
  isATitleBox,
  reinitializeBoxWidthAndHeight,
} from '../utils/boxes';
import { indentTitles, mutateResizeSummary, mutateTitlesIndexes } from '../utils/titles';
import {
  computeColumnWidth,
  getColumnAvailableWidth,
} from '../utils/columns';
import { columnGapPx, gridUnit } from '../constants/gridConfig';
import { MASKS_TYPE } from '../constants/constants';
import { decorator } from '../components/RichEditor/utils';
import { setColorName, setColorStyle } from '../utils/paletteColors';

export const configurationActions = (set, get) => ({
  // Couleur désignée par son label, pas par son rang : la modale affiche des
  // listes filtrées (sans « transparent ») et des palettes de sections liées,
  // dont les rangs ne sont pas ceux de configuration.colors.
  updateColor: (label, newValue) => {
    set(
      produce((state) => {
        setColorStyle(state.configuration.colors, label, newValue);
      }),
      false,
      `update configuration color ${label}`
    );
  },
  updateHighlightColors: (label, newValue) => {
    set(
      produce((state) => {
        setColorStyle(state.configuration.highlightColors, label, newValue);
      }),
      false,
      `update configuration highlightColor ${label}`
    );
  },

  // Nom donné à une couleur de la palette. Le `label` reste son identifiant
  // (les box s'y réfèrent, cf. mergeConfigurationColors) : seul un champ `name`
  // s'ajoute, et un nom vidé fait retomber l'affichage sur le code hexa.
  // Le sommaire garde sa propre copie de la palette : elle est renommée en même
  // temps, sinon la même couleur porterait deux noms selon l'écran.
  renameColor: (label, name) => {
    set(
      produce((state) => {
        setColorName(state.configuration.colors, label, name);
        setColorName(state.summaryConfiguration?.colors, label, name);
      }),
      false,
      `rename color ${label}`
    );
  },
  renameHighlightColor: (label, name) => {
    set(
      produce((state) => {
        setColorName(state.configuration.highlightColors, label, name);
      }),
      false,
      `rename highlight color ${label}`
    );
  },
  setConfiguration: (values, callback) => {
    set(
      produce((state) => {
        // L'écart entre colonnes est fixe (columnGapPx). Un document réglé à
        // l'époque où le champ « Espacement » l'écrivait garde des box calculées
        // avec l'ancienne valeur, donc plus étroites que leur colonne : on les
        // recale au premier réglage touché, et la valeur stockée est ramenée à
        // la constante plus bas.
        if (state.configuration.columnGap !== columnGapPx) {
          state.boxes = state.boxes.map((box) => {
            const column = state.columns.find((c) => c.id === box.columnId);

            if (!column || column.drawMode) return box;
            // Propriétaire = container top-level, ou ligne imbriquée.
            const owner =
              state.containers.find((c) => c.columnsIds.includes(column.id)) ??
              state.rowContainers.find((c) =>
                c.columnsIds.includes(column.id)
              );
            if (!owner || owner.columnsIds.length === 1) return box;
            return reinitializeBoxWidthAndHeight({
              header: state.masks.find(({ id }) => id === MASKS_TYPE.HEADER.id),
              footer: state.masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id),
              box,
              columnWidth: getColumnAvailableWidth({
                columnId: column.id,
                columns: state.columns,
                containers: state.containers,
                rowContainers: state.rowContainers,
                landscape: state.landscape,
                marginLeft: state.configuration.margins.left * gridUnit,
                marginRight: state.configuration.margins.right * gridUnit,
              }),
            });
          });
        }

        if (
          values.margins &&
          (values.margins.left !== state.configuration.margins.left ||
            values.margins.right !== state.configuration.margins.right)
        ) {
          state.boxes = state.boxes.map((box) => {
            const column = state.columns.find((c) => c.id === box.columnId);

            if ((!column && !box.onlyForm) || column?.drawMode) return box;
            return reinitializeBoxWidthAndHeight({
              header: state.masks.find(({ id }) => id === MASKS_TYPE.HEADER.id),
              footer: state.masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id),
              box,
              columnWidth: box.onlyForm
                ? computeColumnWidth({
                    landscape: state.landscape,
                    marginLeft: values.margins.left * gridUnit,
                    marginRight: values.margins.right * gridUnit,
                  })
                : getColumnAvailableWidth({
                    columnId: column.id,
                    columns: state.columns,
                    containers: state.containers,
                    rowContainers: state.rowContainers,
                    landscape: state.landscape,
                    marginLeft: values.margins.left * gridUnit,
                    marginRight: values.margins.right * gridUnit,
                  }),
            });
          });
        }
        if (
          values.margins &&
          values.indentationTitles !== state.indentationTitles
        ) {
          state.boxes = state.boxes.map((box) =>
            indentTitles({
              box,
              columns: state.columns,
              margins: state.configuration.margins,
              indentationTitles: values.indentationTitles,
            })
          );
        }
        // columnGap forcé : la valeur ne pilote plus rien (l'écart entre
        // colonnes est la constante), et les appelants qui étalent la
        // configuration entière la recopieraient telle quelle.
        state.configuration = { ...state.configuration, ...values, columnGap: columnGapPx };
        if (typeof callback === 'function') callback(state);
      }),
      false,
      `setConfiguration for ${Object.keys(values).join(', ')}`
    );
  },
  setSummaryConfiguration: (values) => {
    set(
      produce((state) => {
        state.summaryConfiguration = {
          ...state.summaryConfiguration,
          ...values,
        };
        mutateResizeSummary(state);
      }),
      false,
      'setSummaryConfiguration'
    );
  },
  setGridConfiguration: (values) => {
    set(
      produce((draft) => {
        draft.gridConfiguration = { ...draft.gridConfiguration, ...values };
      }),
      false,
      'setGridConfiguration'
    );
  },
  changeFontFamilyListInConfig: (newFontFamilyList) => {
    if (newFontFamilyList.length > 0) {
      set(
        produce((draft) => {
          const configKeys = draft.configuration.fontFamilyList.map(
            (f) => f.key
          );
          const newKeys = newFontFamilyList.map((f) => f.key);
          const removedKey = configKeys.find((f) => !newKeys.includes(f));
          if (removedKey) {
            const removedFontFamily = draft.configuration.fontFamilyList.find(
              (font) => font.key === removedKey
            );
            //selection 1st family of the list uppon delete configuration.fontFamily
            if (
              draft.configuration.fontFamily === removedFontFamily.familyName
            ) {
              draft.configuration.fontFamily = newFontFamilyList[0].familyName;
            }
            //modification family title in configuration uppon main family delete
            if (
              draft.configuration.title1.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.configuration.title1.fontFamily =
                draft.configuration.fontFamily;
            }
            if (
              draft.configuration.title2.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.configuration.title2.fontFamily =
                draft.configuration.fontFamily;
            }
            if (
              draft.configuration.title3.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.configuration.title3.fontFamily =
                draft.configuration.fontFamily;
            }
            if (
              draft.configuration.title4.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.configuration.title4.fontFamily =
                draft.configuration.fontFamily;
            }
            //modification family title in summaryConfiguration uppon main family delete
            if (
              draft.summaryConfiguration.title1.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.summaryConfiguration.title1.fontFamily =
                draft.summaryConfiguration.fontFamily;
            }
            if (
              draft.summaryConfiguration.title2.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.summaryConfiguration.title2.fontFamily =
                draft.summaryConfiguration.fontFamily;
            }
            if (
              draft.summaryConfiguration.title3.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.summaryConfiguration.title3.fontFamily =
                draft.summaryConfiguration.fontFamily;
            }
            if (
              draft.summaryConfiguration.title4.fontFamily ===
              removedFontFamily.familyName
            ) {
              draft.summaryConfiguration.title4.fontFamily =
                draft.summaryConfiguration.fontFamily;
            }
            draft.configuration.checkboxStyles.forEach((cStyle) => {
              if (cStyle.fontFamily === removedFontFamily.familyName) {
                cStyle.fontFamily = draft.configuration.fontFamily;
              }
            });
            draft.configuration.textStyles.forEach((tStyle) => {
              if (tStyle.fontFamily === removedFontFamily.familyName) {
                tStyle.fontFamily = draft.configuration.fontFamily;
              }
            });
            draft.configuration.selectorStyles.forEach((sStyle) => {
              if (sStyle.fontFamily === removedFontFamily.familyName) {
                sStyle.fontFamily = draft.configuration.fontFamily;
              }
            });
            const defaultProperty = newFontFamilyList.find(
              (f) => f.familyName === draft.configuration.fontFamily
            );
            draft.boxes
              .filter((box) => isATextBox(box.type) || isATitleBox(box.type))
              .forEach((box) => {
                const newEditorState = setDefaultInlineStyleIfNeeded(
                  box.content.editorState,
                  removedFontFamily.key,
                  defaultProperty.key,
                  'FONT_FAMILY_'
                );
                box.content.editorState = EditorState.createWithContent(
                  newEditorState.getCurrentContent(),
                  decorator
                );
              });
            if (
              draft.configuration.fontFamily === removedFontFamily.familyName
            ) {
              draft.configuration.fontFamily = defaultProperty;
            }
          }
          draft.configuration.fontFamilyList = newFontFamilyList;
        }),
        false,
        `Change font Family list in Config and SummaryConfig`
      );
    }
  },
  setDisplayChapterSeparation: (value) => {
    set(
      produce((state) => {
        state.configuration.displayChapterSeparation = value;
        // Recompute all title indices since exclusion rules change with this flag
        mutateTitlesIndexes(state);
        // If firstVisibleContainerId points to a container that is now hidden
        // after the toggle, resolve it to the linked container so the next
        // preview generation starts at the correct chapter.
        if (state.firstVisibleContainerId) {
          const currentContainer = state.containers.find(
            (c) => c.id === state.firstVisibleContainerId
          );
          if (currentContainer?.linkedContainerId) {
            const isChapterSepMask = state.columns.some(
              (col) =>
                currentContainer.columnsIds.includes(col.id) &&
                col.maskId &&
                state.masks.find((m) => m.id === col.maskId)?.chapterSeparation
            );
            const isLinkedTitle = state.boxes.some(
              (box) =>
                currentContainer.columnsIds.includes(box.columnId) &&
                box.linkedToMaskId
            );
            // Mask hidden when value=false; linked title hidden when value=true
            if ((!value && isChapterSepMask) || (value && isLinkedTitle)) {
              state.firstVisibleContainerId = currentContainer.linkedContainerId;
            }
          }
        }
      }),
      false,
      'setDisplayChapterSeparation'
    );
  },
});
